import isEmpty from 'lodash/isEmpty';
import { createSelector } from 'reselect';
import { storeSingleton } from 'src/client/store/storeSingleton';
import { api } from '../../../client/api';
import { getConsentLastUpdatedAt, hasConsentInteraction } from '../../consent/oneTrustCookies';
import { buildPrivacyContext } from '../../consent/privacyContext';
import { AB_TEST_IDS } from '../../constants/experiments';
import { adFormatNameMap, reportingAdFormats } from '../../constants/reporting/adDisplayFormats';
import {
  selectConsentJurisdiction,
  selectIsOptedOutOfTargeting,
  selectSessionId,
  selectUserIpAddress,
  selectUsPrivacyString,
} from '../../selectors/app';
import { selectIsAuthenticated } from '../../selectors/auth';
import {
  selectCountryCode,
  selectCountryStateCode,
  selectExperiment,
} from '../../selectors/config';
import { selectUsername } from '../../selectors/me';

/**
 * Creates the reporting context that every Unified Events API request should include.
 *
 * The privacy block's cookie-derived inputs are read here rather than declared as inputs, because
 * no selector observes them. They only change alongside a consent change, which does move the
 * store — and OneTrust reloads the page on one anyway.
 */
export const updateReportingContext = createSelector(
  [
    selectIsAuthenticated,
    selectSessionId,
    selectUserIpAddress,
    selectUsername,
    selectConsentJurisdiction,
    selectIsOptedOutOfTargeting,
    selectUsPrivacyString,
    selectCountryCode,
    selectCountryStateCode,
    (state) => selectExperiment(state, AB_TEST_IDS, ''),
  ],
  () => {
    const reduxState = storeSingleton.state;
    const isAuthenticated = selectIsAuthenticated(reduxState);
    const sessionId = selectSessionId(reduxState);
    const ip = selectUserIpAddress(reduxState);
    const userId = selectUsername(reduxState);
    const abTestIds = selectExperiment(reduxState, AB_TEST_IDS, '');

    api.updateReportingData({
      context: {
        sessionId,
        ip,
        appState: {
          abTestIds,
        },
        userInfo: {
          userId,
          isRegistered: isAuthenticated,
          isLoggedIn: isAuthenticated,
        },
        privacy: buildPrivacyContext({
          jurisdiction: selectConsentJurisdiction(reduxState),
          countryCode: selectCountryCode(reduxState),
          stateCode: selectCountryStateCode(reduxState),
          isOptedOutOfTargeting: selectIsOptedOutOfTargeting(reduxState),
          usPrivacyString: selectUsPrivacyString(reduxState),
          hasInteracted: hasConsentInteraction(),
          lastUpdatedAt: getConsentLastUpdatedAt(),
        }),
      },
    });
  },
);

/**
 * Returns dimensions value (array of reportingAdFormats enums || reportingAdFormats enum)...
 * to be sent as value of ad_format property in Unified Events API request data
 * @param dimensions {array} - can be an array of arrays (e.g [300, 250] || [[300, 250], [700, 200]])
 * @param returnArray {bool} optional - func will return enums in an array (returns single enum as string by default)
 * @param string (or array) {enum via reportingAdFormats}
 */
export function formatDimensions(dimensions, returnArray = false) {
  if (isEmpty(dimensions)) {
    return returnArray
      ? [reportingAdFormats.AD_DISPLAY_FORMAT_UNSPECIFIED]
      : reportingAdFormats.AD_DISPLAY_FORMAT_UNSPECIFIED;
  }

  if (returnArray) {
    return dimensions.reduce((acc, currentItem, index) => {
      const stringedDimension = currentItem.join('x');
      const dimensionEnum = adFormatNameMap[stringedDimension];

      if (dimensionEnum) {
        acc.push(dimensionEnum);
      }

      if (index === dimensions.length - 1 && isEmpty(acc)) {
        acc.push(reportingAdFormats.AD_DISPLAY_FORMAT_UNSPECIFIED);
      }

      return acc;
    }, []);
  }

  if (
    dimensions.length === 2 &&
    typeof dimensions[0] === 'number' &&
    typeof dimensions[1] === 'number'
  ) {
    return adFormatNameMap[dimensions.join('x')];
  }
}
